import React, {useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import constants from 'helpers/constants';
import DialogContent from 'components/atoms/Dialogs/DialogContent/DialogContent';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import DialogFooter from 'components/molecules/Dialogs/DialogFooter/DialogFooter';
import Button from 'components/atoms/Buttons/Button/Button';
import DialogHeader from 'components/molecules/Dialogs/DialogHeader/DialogHeader';
import {Span} from 'components/atoms/Text/Typography/Typography';
import utils from 'helpers/utils';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Save from '@mui/icons-material/Save';
import Close from '@mui/icons-material/Close';
import StyledCommentDialog from 'components/organisms/Dialogs/CommentDialog/CommentDialog.styles';
import {useClientCallbacks} from 'services/client/client.utils';
import ConfirmDialog from 'components/organisms/Dialogs/ConfirmDialog/ConfirmDialog';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';

const CommentDialog = React.forwardRef((props, ref) => {
  const {
    text,
    onChange,
    onSubmit,
    SaveButtonProps,
    ...innerProps
  } = useComponentProps(props, 'CommentDialog');

  const innerRef = useRef(null);
  const formRef = useRef(null);

  const dialogControl = useDialogControl();

  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(null);
  const [validation, setValidation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showMenu, setShowMenu] = useState(Boolean(innerProps.showMenu));

  useImperativeHandle(ref, () => innerRef.current);
  const clientCallbacks = useClientCallbacks();

  const fields = useMemo(() => ([{
    name: 'comment',
    placeholder: 'Type your comment',
    type: constants.formFieldTypes.richText,
    validation: constants.formFieldValidationTypes.text,
    initial: text,
    required: true,
    FormFieldProps: {
      autoFocus: true,
      hiddenLabel: true,
      variant: 'outlined',
      minRows: 8,
      maxRows: 16,
      mentions: [{
        trigger: '@',
        data: (search, callback) => clientCallbacks.members({search, callback})
      }],
      InputLabelProps: {
        shrink: true,
      },
      InputProps: {
        disableUnderline: true
      },
      inputProps: {
        showMenu: showMenu,
        onToggle: () => {
          setShowMenu((current) => !current);
        }
      }
    }
  }]), [clientCallbacks, text, showMenu]);

  const handleChange = (field, value) => {
    onChange?.(field, value);
  };

  const handleSubmit = (values, actions) => {
    setSubmitting(true);

    utils.asPromise(onSubmit)(values['comment'])
      .then(() => {
        innerProps.onClose?.(null, 'saveButtonClick');
      })
      .catch(() => {
        setError('Saving comment failed');
      })
      .finally(() => {
        actions.setSubmitting(false);
        setSubmitting(false);
      });
  };

  const handleValidating = (isValidating, isDirty, hasErrors) => {
    setDirty(isDirty);
    if (hasErrors) {
      setValidation('Please check if all fields have the correct values');
    } else {
      setValidation(null);
    }
  }

  const handleSubmitClick = () => {
    formRef.current?.submit();
  };

  const handleCancel = (e) => {
    innerRef.current?.close?.(e);
  }

  const handleClose = (e, reason) => {
    if (!dirty || ['saveButtonClick'].includes(reason)) {
      innerProps.onClose?.(e, reason);
    } else if (['escapeKeyDown', 'closeButtonClick', 'cancelButtonClick'].includes(reason)) {
      const handleConfirm = () => {
        innerProps.onClose?.(e, reason);
      }

      dialogControl.show(<ConfirmDialog question="Are you sure you want to exit this dialog?"
                                        explanation="The dialog will close and your data will be lost"
                                        onConfirm={handleConfirm}/>, true);
    }
  }

  const renderButtons = () => {
    return <React.Fragment>
      <Button children={'Cancel'}
              variant="text"
              startIcon={<Icon icon={Close}/>}
              onClick={handleCancel}/>
      <Button disabled={submitting}
              type="submit"
              variant="contained"
              children={'Save'}
              startIcon={<Icon icon={Save}/>}
              onClick={handleSubmitClick}
              {...SaveButtonProps}/>
    </React.Fragment>
  }

  return <StyledCommentDialog ref={innerRef} {...innerProps} onClose={handleClose}>
    <DialogHeader title="Comment" />
    <DialogContent>
      <InlineForm ref={formRef} fields={fields}
                  onValidating={handleValidating}
                  onChange={handleChange}
                  onSubmit={handleSubmit} />
    </DialogContent>
    <DialogFooter className="CommentDialog-footer"
                  info={(error ?? validation) ? <Span color="error">{error ?? validation}</Span> : null}
                  buttons={renderButtons()} />
  </StyledCommentDialog>
});

CommentDialog.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  text: PropTypes.string,
  showMenu: PropTypes.bool,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  SaveButtonProps: PropTypes.object
};

export default CommentDialog;
